import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, dateTimeOf, label, timeOf } from '../../lib/api';
import { useStoreEvents } from '../../lib/events';
import { useBase, useScope } from '../../scope';
import { BookingActions, RefusalBox, StatusChip, type BookingTransition } from './actions';
import { isLive, refusalOf, type AmendBookingInput, type Booking, type DiningTable, type Refusal } from './types';

type AmendForm = { bookingDate: string; bookingTime: string; pax: string; tableId: string; notes: string };

const formOf = (b: Booking): AmendForm => ({ bookingDate: b.bookingDate, bookingTime: b.bookingTime, pax: String(b.pax), tableId: b.tableId ?? '', notes: b.notes ?? '' });

/** One booking: who and when, the amend form while it is live, and the
 * event timeline the server keeps (placed, accepted, amended, …). */
export function BookingDetail() {
  const { rid, sid, store } = useScope();
  const base = useBase();
  const { bookingId = '' } = useParams();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [tables, setTables] = useState<DiningTable[]>([]);
  const [form, setForm] = useState<AmendForm | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, ts] = await Promise.all([api.get<Booking>(`${base}/bookings/${bookingId}`), api.get<DiningTable[]>(`${base}/tables`)]);
      setBooking(b);
      setTables(ts.filter((t) => t.active || t.tableId === b.tableId));
      return b;
    } catch (e) {
      setRefusal(refusalOf(e, 'Failed to load.'));
      return null;
    }
  }, [base, bookingId]);

  useEffect(() => {
    void load().then((b) => b && setForm(formOf(b)));
  }, [load]);
  // live: reload the booking, keep the form the user may be editing
  useStoreEvents(base, ['bookings'], () => void load());

  async function act(fn: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    try {
      await fn();
      const b = await load();
      if (b) setForm(formOf(b));
      setRefusal(null);
      return true;
    } catch (e) {
      setRefusal(refusalOf(e, 'Action failed.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const transition = (event: BookingTransition, reason?: string) =>
    void act(() => api.post(`${base}/bookings/${bookingId}/${event}`, event === 'reject' || event === 'cancel' ? { reason: reason ?? null } : {}));

  async function amend() {
    if (!form || !booking) return;
    const body: AmendBookingInput = {
      bookingDate: form.bookingDate,
      bookingTime: form.bookingTime,
      pax: Number(form.pax) || 0,
      tableId: form.tableId, // "" clears the table
      notes: form.notes,
    };
    if (await act(() => api.put<Booking>(`${base}/bookings/${bookingId}`, body))) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  }

  if (!booking) return <div className="loading">{refusal?.message ?? 'Loading…'}</div>;
  const b = booking;
  const tz = store?.timezone;
  const live = isLive(b.status);

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center', gap: 16 }}>
        <div>
          <div className="overline">
            <Link to={`/r/${rid}/s/${sid}/bookings`}>Bookings</Link> · placed {dateTimeOf(b.placedAt, tz)}
            {b.statusReason ? ` · ${b.statusReason}` : ''}
          </div>
          <h1 style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span>{b.customerName}</span>
            <StatusChip status={b.status} />
            <span className="src" data-source={b.source}>{label(b.source)}</span>
          </h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <BookingActions status={b.status} busy={busy} onAction={transition} />
        </div>
      </div>
      <RefusalBox refusal={refusal} />

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="panel" data-section="booking">
            <h2>Booking</h2>
            <div className="rowline">
              <div className="grow"><b>When</b><span className="sub" data-when>{b.bookingDate} at {b.bookingTime}{b.sessionNo ? ` · session ${b.sessionNo}` : ''}</span></div>
              <span className="tm" data-pax={b.pax}>{b.pax} pax</span>
            </div>
            <div className="rowline">
              <div className="grow"><b>Table</b><span className="sub" data-table>{b.tableName ?? 'Not assigned'}</span></div>
            </div>
            <div className="rowline">
              <div className="grow"><b>Guest</b><span className="sub">{b.customerPhoneE164}{b.customerEmail ? ` · ${b.customerEmail}` : ''}</span></div>
            </div>
            {b.notes && (
              <div className="rowline"><div className="grow"><b>Notes</b><span className="sub" data-notes>{b.notes}</span></div></div>
            )}
          </div>

          <div className="panel" data-section="amend">
            <h2>Amend {saved && <span style={{ color: 'var(--ok)', textTransform: 'none', letterSpacing: 0 }}>· saved ✓</span>}</h2>
            {form && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, paddingTop: 8 }}>
                  <div className="field"><label>Date</label>
                    <input className="input" type="date" aria-label="Booking date" disabled={!live} value={form.bookingDate} onChange={(e) => setForm({ ...form, bookingDate: e.target.value })} /></div>
                  <div className="field"><label>Time</label>
                    <input className="input" type="time" aria-label="Booking time" disabled={!live} value={form.bookingTime} onChange={(e) => setForm({ ...form, bookingTime: e.target.value })} /></div>
                  <div className="field"><label>Guests</label>
                    <input className="input" type="number" min={1} aria-label="Guests" disabled={!live} value={form.pax} onChange={(e) => setForm({ ...form, pax: e.target.value })} /></div>
                  <div className="field"><label>Table</label>
                    <select className="input" aria-label="Table" disabled={!live} value={form.tableId} onChange={(e) => setForm({ ...form, tableId: e.target.value })}>
                      <option value="">No table</option>
                      {tables.map((t) => <option key={t.tableId} value={t.tableId}>{t.name}{t.maxGuests ? ` (seats ${t.maxGuests})` : ''}</option>)}
                    </select></div>
                </div>
                <div className="field" style={{ marginTop: 12 }}><label>Notes</label>
                  <textarea className="input" rows={2} aria-label="Notes" disabled={!live} value={form.notes} placeholder="Window seat, highchair…" onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}>
                  <button className="btn btn-primary" disabled={busy || !live} onClick={() => void amend()}>{busy ? 'Saving…' : 'Save changes'}</button>
                  {!live && <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>This booking is {b.status} — it can no longer be amended.</span>}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="panel" data-section="timeline">
          <h2>Timeline</h2>
          {b.events.length === 0 && <div className="loading">No events recorded.</div>}
          {b.events.map((e) => (
            <div key={e.seq} className="rowline" data-event={e.eventType}>
              <span className="tm">{timeOf(e.occurredAt, tz)}</span>
              <div className="grow">
                <b>{label(e.eventType)}</b>
                <span className="sub">{label(e.actorType)}{e.note ? ` · ${e.note}` : ''}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
